import { ProductKey } from "../types/billing";
import { isProductKey } from "./productCatalogue";

/**
 * A note, left in this tab's sessionStorage just before the browser goes to Stripe, that a
 * purchase is in flight, what it buys and which product it was started from.
 *
 * The trip to Stripe unloads the app, so nothing in memory survives it. On the way back the
 * wallet reads the note to know it should wait for the Credits to land (the webhook arrives
 * after the redirect), and the success page reads it for the name of the product to go back to
 * and for what the purchase adds up to.
 *
 * sessionStorage rather than localStorage: the note belongs to the tab that went to Stripe, and
 * another tab opened later must not act on it.
 */

const STORAGE_KEY = "iiq.purchaseInFlight";

/**
 * Past this the note is ignored. A Stripe Checkout Session expires after a day, so a note older
 * than that describes a purchase that can no longer complete.
 */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface PurchaseInFlight {
  /** The product the dialog was opened from, if it was opened from one. */
  returnProduct: ProductKey | null;
  /** That product's name as the catalogue gave it, for "Back to …" on the success page. */
  returnName: string | null;
  /** The Credits the purchase adds to the wallet. */
  credits: number | null;
  /**
   * What one run of `returnProduct` costs, for "That's enough for N runs": for Multi-Entity
   * SignalScan, a scan at the number of companies the dialog was set to.
   */
  perRunCredits: number | null;
  startedAt: number;
}

/** What the purchase buys, as far as the dialog knows it. */
export interface PurchaseAmounts {
  credits?: number | null;
  perRunCredits?: number | null;
}

/** A whole number of 1 or more, or `null`: anything else cannot be counted in runs. */
const positiveCount = (value: unknown): number | null =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 1
    ? value
    : null;

export const markPurchaseInFlight = (
  returnProduct: ProductKey | null,
  returnName: string | null,
  amounts: PurchaseAmounts = {},
  now: number = Date.now(),
): void => {
  const note: PurchaseInFlight = {
    returnProduct,
    returnName,
    credits: positiveCount(amounts.credits),
    perRunCredits: positiveCount(amounts.perRunCredits),
    startedAt: now,
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(note));
  } catch {
    // Storage can be full or switched off. The purchase goes ahead either way; what is lost is
    // the wait for the Credits on the way back, and a reload shows them.
  }
};

/** The note, or `null` when there is none, it does not read, or it is too old to matter. */
export const readPurchaseInFlight = (
  now: number = Date.now(),
): PurchaseInFlight | null => {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const note = JSON.parse(raw) as Partial<PurchaseInFlight> | null;
    if (!note || typeof note.startedAt !== "number") return null;
    if (now - note.startedAt > MAX_AGE_MS) return null;
    return {
      returnProduct: isProductKey(note.returnProduct)
        ? note.returnProduct
        : null,
      returnName:
        typeof note.returnName === "string" && note.returnName !== ""
          ? note.returnName
          : null,
      // Checked again on the way out: the note is only as trustworthy as anything else in the
      // tab, and the success page prints these numbers.
      credits: positiveCount(note.credits),
      perRunCredits: positiveCount(note.perRunCredits),
      startedAt: note.startedAt,
    };
  } catch {
    return null;
  }
};

export const clearPurchaseInFlight = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear where storage cannot be reached.
  }
};

/** Read the note and remove it, so a reload does not act on it a second time. */
export const takePurchaseInFlight = (
  now: number = Date.now(),
): PurchaseInFlight | null => {
  const note = readPurchaseInFlight(now);
  clearPurchaseInFlight();
  return note;
};
